<template>
    <section class="object-group">
        <header class="object-group__header">
            <span>{{ label }}</span>
            <Button
                v-if="editing && held !== null"
                label="Clear"
                severity="secondary"
                variant="text"
                size="small"
                @click="clear"
            />
            <Button
                v-else-if="editing"
                label="Add"
                icon="pi pi-plus"
                severity="secondary"
                variant="text"
                size="small"
                @click="start"
            />
        </header>

        <p v-if="held === null" class="object-group__empty">
            None. <template v-if="!editing">Edit to add one.</template>
        </p>

        <div v-else class="object-group__fields">
            <div v-for="path in paths" :key="path" class="object-group__field">
                <small>{{ leafLabel(path) }}</small>
                <FieldControl
                    v-if="editing"
                    :field-key="path"
                    :field-definitions="fieldDefinitions"
                    :field-meta="fieldMeta"
                    :value="read(path)"
                    @update:value="(value) => write(path, value)"
                />
                <FormatValue
                    v-else
                    class="object-group__value"
                    :record="draft"
                    :field-key="path"
                    :field-definitions="fieldDefinitions"
                    :field-meta="fieldMeta"
                />
            </div>
        </div>
    </section>
</template>

<script setup>
import Button from 'primevue/button'
import { computed } from 'vue'

import FieldControl from '@/admin-collection-layout/lib/FieldControl.vue'
import { FormatValue } from '@/admin-collection-layout/lib/formatValue.js'
import { labelFor } from '@/lib/fieldLabels.js'

// A nested property, drawn from the leaves the searchable surface publishes.
//
// A Company's billing facts and an Auction House's address are objects, so
// neither the field controls nor the surface offers them as one control. But
// the surface carries every leaf beneath them as a dotted path — and those
// paths are the same ones a query names — so the group is built from them and
// the object is reassembled on the way out.
//
// Clearing sets the key to `null` rather than removing it, which is the one
// thing a PATCH body distinguishes from an absent key: `null` clears, absent
// leaves alone.
const props = defineProps({
    objectKey: { type: String, required: true },
    label: { type: String, required: true },
    draft: { type: Object, default: null },
    fieldDefinitions: { type: Object, required: true },
    fieldMeta: { type: Object, default: () => ({}) },
    editing: { type: Boolean, default: false },
    setField: { type: Function, required: true },
    // Where a postal address sits inside the object, or '' for none. Its leaves
    // are left to the address editor drawn beneath, rather than listed twice.
    addressAt: { type: String, default: '' }
})

// Identifier leaves are not drawn: a hex key or a Stripe customer key means
// nothing to somebody reading billing facts. `write` clones the whole object,
// so they are still saved with it.
const paths = computed(function () {
    const prefix = `${props.objectKey}.`
    const address = props.addressAt === '' ? null : `${prefix}${props.addressAt}.`
    return Object.keys(props.fieldDefinitions.fields ?? {}).filter((path) => path.startsWith(prefix)
        && (address === null || !path.startsWith(address))
        && props.fieldMeta[path]?.identifier !== true)
})

const held = computed(() => props.draft?.[props.objectKey] ?? null)

function read(path) {
    let cursor = props.draft
    for (const segment of path.split('.')) {
        if (cursor === null || cursor === undefined) {
            return null
        }
        cursor = cursor[segment]
    }
    return cursor ?? null
}

/**
 * Written back through one `setField` on the top key, because that is all the
 * panel's draft understands — it sets whole keys, not paths. So the object is
 * cloned, the leaf placed, and the whole thing handed back.
 */
function write(path, value) {
    const [top, ...rest] = path.split('.')
    // A JSON round-trip rather than `structuredClone`, which throws
    // `DataCloneError` on a Vue reactive proxy — silently, inside an event
    // handler, so every leaf typed here was discarded and the object saved
    // empty. These are plain documents off the wire, so the round-trip is exact.
    const next = JSON.parse(JSON.stringify(props.draft?.[top] ?? {}))
    let cursor = next
    for (const segment of rest.slice(0, -1)) {
        if (cursor[segment] === undefined || cursor[segment] === null) {
            cursor[segment] = {}
        }
        cursor = cursor[segment]
    }
    cursor[rest[rest.length - 1]] = value
    props.setField(top, next)
}

function clear() {
    props.setField(props.objectKey, null)
}

function start() {
    props.setField(props.objectKey, {})
}

/**
 * `billing.address.city` reads as "Address city" inside a group called Billing.
 *
 * Built from the path, not from the label. `fieldMeta` is keyed by the leaf
 * paths the searchable surface publishes and nothing else, so there is no
 * `fieldMeta['billing']` to take a prefix from — every row used to fall through
 * and read "Billing address city" under a "Billing facts" header.
 */
function leafLabel(path) {
    return labelFor(path.slice(props.objectKey.length + 1))
}
</script>

<style scoped>
.object-group {
    border-top: 1px solid var(--p-content-border-color);
    margin-top: 12px;
    padding-top: 12px;
}

.object-group__header {
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: space-between;
}

.object-group__header span {
    font-size: 13px;
    font-weight: 600;
}

.object-group__empty {
    color: var(--p-surface-400);
    font-size: 12px;
    margin: 4px 0 0;
}

.object-group__fields {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
}

.object-group__field {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.object-group__field small {
    color: var(--p-surface-400);
    font-size: 11px;
}

.object-group__value {
    font-size: 13px;
}
</style>
